/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-10
 */

// Add third-party dependencies.
import { Box, Skeleton, Paper } from '@mui/material';

/**
 * Props for the ProfileFormSkeleton component.
 */
interface ProfileFormSkeletonProps {
  /**
   * Number of form fields to show skeletons for.
   * @default 4
   */
  fieldCount?: number;
  /**
   * Whether to show action buttons skeleton.
   * @default true
   */
  showButtons?: boolean;
  /**
   * Whether to show a title skeleton.
   * @default true
   */
  showTitle?: boolean;
  /**
   * Height of each field skeleton.
   * @default 56
   */
  fieldHeight?: number;
}

/**
 * ProfileFormSkeleton component that displays loading skeletons for profile forms.
 * Used while profile data is being fetched from the API.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The profile form skeleton component
 *
 * @example
 * <ProfileFormSkeleton fieldCount={6} />
 */
const ProfileFormSkeleton: React.FC<ProfileFormSkeletonProps> = ({
  fieldCount = 4,
  showButtons = true,
  showTitle = true,
  fieldHeight = 56,
}) => (
  <Paper sx={{ p: 3 }}>
    {/* Title Skeleton */}
    {showTitle && (
      <Skeleton
        variant="text"
        width="40%"
        height={32}
        sx={{ mb: 1 }}
        animation="wave"
      />
    )}

    {/* Subtitle Skeleton */}
    {showTitle && (
      <Skeleton
        variant="text"
        width="60%"
        height={20}
        sx={{ mb: 3 }}
        animation="wave"
      />
    )}

    {/* Form Fields Skeletons */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Array.from({ length: fieldCount }, (_, index) => (
        <Skeleton
          key={`field-skeleton-${index}`}
          variant="rectangular"
          width="100%"
          height={fieldHeight}
          sx={{
            borderRadius: 1,
            '&:nth-of-type(odd)': {
              width: '85%', // Vary widths for more realistic look
            },
          }}
          animation="wave"
        />
      ))}
    </Box>

    {/* Action Buttons Skeleton */}
    {showButtons && (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          mt: 4,
        }}
      >
        <Skeleton
          variant="rectangular"
          width={100}
          height={36}
          sx={{ borderRadius: 1 }}
          animation="wave"
        />
        <Skeleton
          variant="rectangular"
          width={140}
          height={36}
          sx={{ borderRadius: 1 }}
          animation="wave"
        />
      </Box>
    )}
  </Paper>
);

export default ProfileFormSkeleton;
